import { RefObject, useEffect, useState } from 'react';

export function useCloseOnOutsideOrEscape(
  ref: RefObject<HTMLElement | null>,
  isOpen: boolean,
  setIsOpen: (isOpen: boolean) => void
) {
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const closeIfOutside = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('pointerdown', closeIfOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeIfOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [ref, isOpen, setIsOpen]);
}

const PIXELS_PER_WHEEL_LINE = 16;

export function useWheelScrollsSideways(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    const scrollSideways = (e: WheelEvent) => {
      const delta =
        e.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? e.deltaY * PIXELS_PER_WHEEL_LINE
          : e.deltaY;
      if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) {
        return;
      }
      const maxScrollLeft = element.scrollWidth - element.clientWidth;
      const canScroll =
        delta < 0
          ? element.scrollLeft > 0
          : element.scrollLeft < maxScrollLeft - 1;
      if (!canScroll) {
        return;
      }
      e.preventDefault();
      element.scrollLeft += delta;
    };
    element.addEventListener('wheel', scrollSideways, { passive: false });
    return () => element.removeEventListener('wheel', scrollSideways);
  }, [ref]);
}

export function useIdHash(id: string) {
  const [idHash, setIdHash] = useState<string>();
  useEffect(() => {
    let isCurrent = true;
    crypto.subtle
      ?.digest('SHA-256', new TextEncoder().encode(id))
      .then((digest) => {
        if (isCurrent) {
          setIdHash(
            Array.from(new Uint8Array(digest), (byte) =>
              byte.toString(16).toUpperCase().padStart(2, '0')
            ).join('-')
          );
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [id]);
  return idHash;
}
